import { useCallback, useEffect, useRef } from 'react'
import { useReactiveVar } from '@apollo/client'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { UpdateProjectContent } from 'api/graphQL/reactiveVars/workspaceContent'
import { ContentInsertVariables, GetContentInsertStatusResponse } from 'api/graphQL/types/contentInsert.types'
import {
    useFamilyContentInsertStatusQuery,
    useProductTypeContentInsertStatusQuery,
} from '../api/queries/copyPasteContentQueries'

const StatusText_Importing: string =
    "Specpoint is importing your content into the database. This shouldn't take long..."
const STATUS_IN_PROGRESS = 'INPROGRESS'
const STATUS_COMPLETE = 'COMPLETE'
const STATUS_ERROR = 'ERROR'

interface UseInsertStatusPollerProps {
    contentInsertVariables: Partial<ContentInsertVariables>
    instanceId: string | undefined
    isPasteInProgress: boolean
    progress: number
    setProgress: (progress: number) => void
    setStatusText: (statusText: string) => void
    setIsPasteInProgress: (isPasteInProgress: boolean) => void
    onComplete: (result: Partial<UpdateProjectContent>) => void
    onError: (errorMessage: string | null | undefined) => void
}

export const useInsertStatusPoller = ({
    contentInsertVariables,
    instanceId,
    isPasteInProgress,
    progress,
    setProgress,
    setStatusText,
    setIsPasteInProgress,
    onComplete,
    onError,
}: UseInsertStatusPollerProps) => {
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    const onStatusQueryCompleted = useCallback(
        (data: GetContentInsertStatusResponse) => {
            const contentInsertState = data?.getContentInsertStatus?.state?.toUpperCase()
            switch (contentInsertState) {
                case STATUS_IN_PROGRESS:
                    const progressPercentage =
                        data?.getContentInsertStatus?.progressPercentage
                    if (
                        progressPercentage !== undefined &&
                        progressPercentage !== progress
                    ) {
                        setProgress(progressPercentage)
                    }
                    // still in progress; poll again after a short time
                    timeoutRef.current = setTimeout(() => queryForStatus(), 1000)
                    break
                case STATUS_COMPLETE:
                    setProgress(100)
                    setIsPasteInProgress(false)
                    const result =
                        data?.getContentInsertStatus?.updatedProjectContent
                    if (result) {
                        setTimeout(() => onComplete(result), 500)
                    }
                    break
                case STATUS_ERROR:
                    setIsPasteInProgress(false)
                    onError(data?.getContentInsertStatus?.errorMessage)
                    break
            }
        },
        [progress, onComplete, onError, setProgress, setIsPasteInProgress]
    )

    const {
        fetchFamilyContentInsertStatus,
    } = useFamilyContentInsertStatusQuery({
        instanceId: instanceId ?? '',
        partType: contentInsertVariables.partType ?? '',
        rootArticleContentItemIds:
            contentInsertVariables.rootArticleContentItemIds ?? [],
        productFamilyId: contentInsertVariables.productFamilyId ?? '',
        onCompleted: onStatusQueryCompleted,
        onError: (error) => onError(error.message),
    })

    const {
        fetchProductTypeContentInsertStatus,
    } = useProductTypeContentInsertStatusQuery({
        instanceId: instanceId ?? '',
        partType: contentInsertVariables.partType ?? '',
        rootArticleContentItemIds:
            contentInsertVariables.rootArticleContentItemIds ?? [],
        productFamilyId: contentInsertVariables.productFamilyId ?? '',
        nodeId: selectedProjectElementNode?.id ?? '',
        onCompleted: onStatusQueryCompleted,
        onError: (error) => onError(error.message),
    })

    const queryForStatus = useCallback(() => {
        if (
            selectedProjectElementNode?.elementType ===
            ProjectElementType.PRODUCTTYPE
        ) {
            fetchProductTypeContentInsertStatus()
        } else {
            fetchFamilyContentInsertStatus()
        }
    }, [
        selectedProjectElementNode,
        fetchProductTypeContentInsertStatus,
        fetchFamilyContentInsertStatus,
    ])

    useEffect(() => {
        if (instanceId && isPasteInProgress) {
            setStatusText(StatusText_Importing)
            queryForStatus()
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [instanceId, isPasteInProgress, queryForStatus, setStatusText])

    return {
        queryForStatus,
    }
}